import { useCallback, useEffect, useState } from "react";
import { LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { fmt, hoursNL, timeOf } from "@/lib/dates";
import { PageHeader, Panel, Loader } from "@/components/common";

const duration = (fromIso, now) => {
  const s = Math.max(0, Math.floor((now - new Date(fromIso)) / 1000));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
};

export default function ClockPage() {
  const api = useApi();
  const [status, setStatus] = useState(null);
  const [now, setNow] = useState(new Date());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/time/status").then((r) => setStatus(r.data)), [api]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const toggle = async () => {
    setBusy(true);
    const isIn = !!status.open_entry;
    try {
      await api.post(isIn ? "/time/clock-out" : "/time/clock-in");
      toast.success(isIn ? "Je bent uitgeklokt. Fijne dag!" : "Je bent ingeklokt. Succes!");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <Loader />;
  const open = status.open_entry;

  return (
    <div data-testid="clock-page">
      <PageHeader title="In- & uitklokken" subtitle={fmt(now, "EEEE d MMMM")} />
      <Panel className="flex flex-col items-center py-10 fade-up">
        <p className="font-mono-num text-5xl sm:text-6xl font-semibold text-slate-900" data-testid="live-clock">{fmt(now, "HH:mm:ss")}</p>
        <p className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${open ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="clock-status">
          <span className={`h-2 w-2 rounded-full ${open ? "bg-emerald-500" : "bg-slate-400"}`} />
          {open ? `Ingeklokt sinds ${timeOf(open.clock_in)}` : "Uitgeklokt"}
        </p>
        <button
          onClick={toggle}
          disabled={busy}
          className={`mt-8 h-52 w-52 sm:h-60 sm:w-60 rounded-full text-white flex flex-col items-center justify-center gap-2 shadow-xl transition-transform duration-200 active:scale-95 hover:scale-[1.03] disabled:opacity-60 ${open ? "bg-[#0D5C46]" : "bg-[#FF5A36] pulse-ring"}`}
          data-testid={open ? "clock-out-button" : "clock-in-button"}
        >
          {open ? <LogOut className="h-10 w-10" /> : <LogIn className="h-10 w-10" />}
          <span className="font-display text-2xl font-bold">{open ? "Uitklokken" : "Inklokken"}</span>
          {open && <span className="font-mono-num text-lg opacity-90" data-testid="shift-timer">{duration(open.clock_in, now)}</span>}
        </button>
        <p className="mt-6 text-sm text-slate-500" data-testid="today-hours">Vandaag gewerkt: <b className="text-slate-800">{hoursNL(status.today_hours)} uur</b></p>
        <p className="mt-2 text-xs text-slate-400 text-center max-w-xs" data-testid="break-hint">Pauze? Klok uit bij het begin van je pauze en klok weer in als je verder gaat.</p>
      </Panel>

      <Panel className="mt-4 fade-up fade-up-2" data-testid="today-entries-panel">
        <h2 className="font-semibold text-lg mb-3">Registraties vandaag</h2>
        {status.today_entries.length === 0 ? (
          <p className="text-sm text-slate-500">Nog geen registraties vandaag.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {status.today_entries.map((e) => (
              <li key={e.id} className="flex justify-between py-2.5 text-sm" data-testid={`today-entry-${e.id}`}>
                <span className="font-mono-num">{timeOf(e.clock_in)} – {e.clock_out ? timeOf(e.clock_out) : "nu"}</span>
                <span className="font-semibold">{e.clock_out ? `${hoursNL(e.hours)} u` : "bezig"}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
